import { useEffect, useRef, useState } from 'react'

interface CommitOptions {
  /**
   * Handle a failure here instead of via the hook's inline `error`. Used when a rejection should not
   * leave an editable-but-invalid field — the Edit Column panel passes this when it commits on close
   * (a toast is the only place left to report), and the header passes it to revert the rename.
   */
  onError?: (error: unknown) => void
}

interface UseColumnRenameOptions {
  /** The column's current saved name. */
  savedName: string
  /** Perform the rename request: resolve on success, reject on failure. */
  save: (name: string) => Promise<void>
  /** Side effects after a successful save (e.g. close the editor, show a toast). */
  onSaved?: (name: string) => void
  /** Derive the inline `error` message from a rejected save. */
  getErrorMessage?: (error: unknown) => string
}

export interface ColumnRename {
  draftName: string
  setDraftName: (name: string) => void
  /** The inline validation message from the last rejected commit, or undefined. */
  error: string | undefined
  /**
   * Save `typedName` (or the current draft). If a save is already in flight it is not raced — that
   * save re-checks the draft when it settles. Returns false only for a true no-op (an empty or
   * unchanged name); true when a save was started or one is already in flight.
   */
  commit: (typedName?: string, options?: CommitOptions) => boolean
  /** Point the draft at a name and clear transient state. Call when the editor opens or is abandoned. */
  reset: (name: string) => void
}

/**
 * Column-rename state and save behaviour shared by the header double-click rename and the Edit
 * Column panel. The name is validated and saved on commit (Enter, blur, panel close), never while
 * typing, and saves are serialized: only one request is in flight at a time, so out-of-order
 * responses can't apply a stale name. A name committed while a save is running is picked up from the
 * live draft and sent once that save settles, so nothing the user typed is lost (issue #10385).
 */
export const useColumnRename = ({
  savedName,
  save,
  onSaved,
  getErrorMessage
}: UseColumnRenameOptions): ColumnRename => {
  const [draftName, setDraftName] = useState<string>(savedName)
  const [error, setError] = useState<string>()
  // The name currently being saved (null when idle) — the serialization guard.
  const pendingRef = useRef<string | null>(null)
  // Mirrors draftName so an in-flight save's callback can read the latest typed value.
  const draftRef = useRef<string>(draftName)

  useEffect(() => {
    draftRef.current = draftName
  }, [draftName])

  const reset = (name: string): void => {
    setDraftName(name)
    draftRef.current = name
    setError(undefined)
    pendingRef.current = null
  }

  // `currentName` is the column's actual name at the moment this save is sent — savedName for the
  // first save in a chain, and the previously-saved name for a follow-up. It can't be read from the
  // `savedName` closure, which is stale once a save in the chain has succeeded.
  const runSave = (trimmed: string, currentName: string, options?: CommitOptions): void => {
    pendingRef.current = trimmed
    save(trimmed)
      .then(() => {
        pendingRef.current = null
        setError(undefined)
        // The column is now `trimmed`. If the user moved on to a different name mid-flight, send that
        // and let ITS completion report success/failure — defer onSaved (which may close the editor)
        // until the latest name settles, so a later rejection of the queued name is still shown inline.
        const next = draftRef.current.trim()
        if (next !== '' && next !== trimmed) {
          runSave(next, trimmed, options)
          return
        }
        onSaved?.(trimmed)
        if (next === trimmed) setDraftName(trimmed) // unchanged — normalise the field to the trimmed value
      })
      .catch((err) => {
        pendingRef.current = null
        // The save failed, so the column is still `currentName`. Retry only a genuinely different
        // name the user typed while this was in flight; otherwise surface the rejection.
        const next = draftRef.current.trim()
        if (next !== '' && next !== trimmed && next !== currentName) {
          runSave(next, currentName, options)
          return
        }
        if (options?.onError) options.onError(err)
        else setError(getErrorMessage?.(err) ?? 'The name could not be saved.')
      })
  }

  const commit = (typedName?: string, options?: CommitOptions): boolean => {
    if (pendingRef.current !== null) return true
    const trimmed = (typedName ?? draftRef.current).trim()
    if (trimmed === '' || trimmed === savedName) return false
    runSave(trimmed, savedName, options)
    return true
  }

  return { draftName, setDraftName, error, commit, reset }
}
